import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { FaChevronLeft, FaChevronRight, FaSearchMinus, FaFilter, FaClock, FaCalendarAlt } from 'react-icons/fa';
import './TimeRangeSelector.scss';

/**
 * Time range presets organized by category
 */
const PRESET_GROUPS = [
  {
    label: 'Live',
    presets: [
      { key: 'realtime', label: 'Realtime', hours: 0.25, realtime: true },
    ]
  },
  {
    label: 'Last',
    presets: [
      { key: '5m', label: 'Last 5 minutes', hours: 5 / 60 },
      { key: '15m', label: 'Last 15 minutes', hours: 0.25 },
      { key: '30m', label: 'Last 30 minutes', hours: 0.5 },
      { key: '1h', label: 'Last 1 hour', hours: 1 },
      { key: '3h', label: 'Last 3 hours', hours: 3 },
      { key: '6h', label: 'Last 6 hours', hours: 6 },
      { key: '12h', label: 'Last 12 hours', hours: 12 },
      { key: '1d', label: 'Last 1 day', hours: 24 },
      { key: '2d', label: 'Last 2 days', hours: 48 },
      { key: '7d', label: 'Last 7 days', hours: 168 },
      { key: '30d', label: 'Last 30 days', hours: 720 },
      { key: '3mo', label: 'Last 3 months', hours: 2160 },
      { key: '6mo', label: 'Last 6 months', hours: 4320 },
      { key: '1y', label: 'Last 1 year', hours: 8760 },
    ]
  },
  {
    label: 'Calendar',
    presets: [
      { key: 'today', label: 'Today', resolve: () => getCalendarRange('today') },
      { key: 'yesterday', label: 'Yesterday', resolve: () => getCalendarRange('yesterday') },
      { key: 'this_week', label: 'This week', resolve: () => getCalendarRange('this_week') },
      { key: 'prev_week', label: 'Previous week', resolve: () => getCalendarRange('prev_week') },
      { key: 'this_month', label: 'This month', resolve: () => getCalendarRange('this_month') },
      { key: 'prev_month', label: 'Previous month', resolve: () => getCalendarRange('prev_month') },
      { key: 'this_year', label: 'This year', resolve: () => getCalendarRange('this_year') },
      { key: 'prev_year', label: 'Previous year', resolve: () => getCalendarRange('prev_year') },
    ]
  },
  {
    label: 'So far',
    presets: [
      { key: 'today_sf', label: 'Today so far', resolve: () => getCalendarRange('today_sf') },
      { key: 'week_sf', label: 'This week so far', resolve: () => getCalendarRange('week_sf') },
      { key: 'month_sf', label: 'This month so far', resolve: () => getCalendarRange('month_sf') },
      { key: 'year_sf', label: 'This year so far', resolve: () => getCalendarRange('year_sf') },
    ]
  }
];

/**
 * Resolves calendar-relative presets to absolute from/to dates
 */
function getCalendarRange(key) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayOfWeek = now.getDay(); // 0=Sun
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  switch (key) {
    case 'today':
      return { from: startOfDay, to: new Date(startOfDay.getTime() + 86400000 - 1) };
    case 'yesterday': {
      const yStart = new Date(startOfDay.getTime() - 86400000);
      return { from: yStart, to: new Date(startOfDay.getTime() - 1) };
    }
    case 'this_week': {
      const wStart = new Date(startOfDay.getTime() - mondayOffset * 86400000);
      const wEnd = new Date(wStart.getTime() + 7 * 86400000 - 1);
      return { from: wStart, to: wEnd };
    }
    case 'prev_week': {
      const pwStart = new Date(startOfDay.getTime() - (mondayOffset + 7) * 86400000);
      const pwEnd = new Date(pwStart.getTime() + 7 * 86400000 - 1);
      return { from: pwStart, to: pwEnd };
    }
    case 'this_month': {
      const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const mEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { from: mStart, to: mEnd };
    }
    case 'prev_month': {
      const pmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const pmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { from: pmStart, to: pmEnd };
    }
    case 'this_year': {
      return { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999) };
    }
    case 'prev_year': {
      return { from: new Date(now.getFullYear() - 1, 0, 1), to: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999) };
    }
    case 'today_sf':
      return { from: startOfDay, to: now };
    case 'week_sf': {
      const wsStart = new Date(startOfDay.getTime() - mondayOffset * 86400000);
      return { from: wsStart, to: now };
    }
    case 'month_sf':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
    case 'year_sf':
      return { from: new Date(now.getFullYear(), 0, 1), to: now };
    default:
      return { from: new Date(now.getTime() - 86400000), to: now };
  }
}

/**
 * Formats date for datetime-local input
 */
function toLocalDatetime(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Returns a compact human-readable date range string for UI display.
 */
function formatRangeForDisplay(range) {
  if (!range?.from || !range?.to) return '';
  const dateFormatOptions = { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' };
  return `${range.from.toLocaleString('en-US', dateFormatOptions)} → ${range.to.toLocaleString('en-US', dateFormatOptions)}`;
}

/**
 * Computes a range object for a given preset key without setting any state.
 */
function computePresetRange(key) {
  for (const group of PRESET_GROUPS) {
    const preset = group.presets.find(p => p.key === key);
    if (preset) {
      if (preset.resolve) {
        const resolved = preset.resolve();
        return { from: resolved.from, to: resolved.to, hours: (resolved.to - resolved.from) / 3600000, key };
      }
      const to = new Date();
      const from = new Date(to.getTime() - preset.hours * 3600000);
      return { from, to, hours: preset.hours, key, realtime: !!preset.realtime };
    }
  }
  return null;
}

/**
 * Returns a human-readable label for the current selection
 */
function getDisplayLabel(selectedKey) {
  if (selectedKey === 'realtime') return '● Realtime';
  for (const group of PRESET_GROUPS) {
    const preset = group.presets.find(p => p.key === selectedKey);
    if (preset) return preset.label;
  }
  return 'Custom range';
}

/**
 * TimeRangeSelector Component
 * Time range picker with preset buttons, custom date inputs,
 * and navigation controls.
 *
 * Supports an imperative handle via React ref:
 *   ref.current.setCustomRange(fromDate, toDate) — programmatically sets a
 *   custom date range (used by the chart drag-to-zoom feature).
 *
 * @param {Object} props
 * @param {Function} props.onChange - Called with { from: Date, to: Date, hours: number, key: string }
 * @param {string} props.initialKey - Initial preset key (default '1d')
 * @param {React.Ref} ref - Optional ref exposing { setCustomRange }
 */
const TimeRangeSelector = forwardRef(({ onChange, initialKey = '1d' }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(initialKey);
  const initialRange = computePresetRange(initialKey);
  const [customFrom, setCustomFrom] = useState(initialRange ? toLocalDatetime(initialRange.from) : '');
  const [customTo, setCustomTo] = useState(initialRange ? toLocalDatetime(initialRange.to) : '');
  const [currentRange, setCurrentRange] = useState(initialRange);
  const panelRef = useRef(null);
  const rangeSummary = formatRangeForDisplay(currentRange);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Applies a preset time range
   */
  const applyPreset = useCallback((key) => {
    let range;
    for (const group of PRESET_GROUPS) {
      const preset = group.presets.find(p => p.key === key);
      if (preset) {
        if (preset.resolve) {
          const resolved = preset.resolve();
          range = { from: resolved.from, to: resolved.to, hours: (resolved.to - resolved.from) / 3600000, key };
        } else {
          const to = new Date();
          const from = new Date(to.getTime() - preset.hours * 3600000);
          range = { from, to, hours: preset.hours, key, realtime: !!preset.realtime };
        }
        break;
      }
    }

    if (range) {
      setSelectedKey(key);
      setCurrentRange(range);
      setCustomFrom(toLocalDatetime(range.from));
      setCustomTo(toLocalDatetime(range.to));
      setIsOpen(false);
      onChange?.(range);
    }
  }, [onChange]);

  // Expose imperative methods so parent components (e.g. chart zoom handlers)
  // can programmatically apply a custom date range without user interaction.
  useImperativeHandle(ref, () => ({
    /**
     * Programmatically sets a custom date range.
     * Updates all internal state (selectedKey, currentRange, inputs) and
     * notifies the parent via onChange, exactly like a user-applied custom range.
     *
     * @param {Date} from - Start of the custom range
     * @param {Date} to   - End of the custom range
     */
    setCustomRange: (from, to) => {
      if (!from || !to || from >= to) return;
      const hours = (to.getTime() - from.getTime()) / 3600000;
      const range = { from, to, hours, key: 'custom' };
      setSelectedKey('custom');
      setCurrentRange(range);
      setCustomFrom(toLocalDatetime(from));
      setCustomTo(toLocalDatetime(to));
      setIsOpen(false);
      onChange?.(range);
    }
  }), [onChange]);

  // Notify parent of the initial range on mount only
  useEffect(() => {
    const range = computePresetRange(initialKey);
    if (range) onChange?.(range);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Applies a custom date range
   */
  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    const from = new Date(customFrom);
    const to = new Date(customTo);
    if (from >= to) return;

    const hours = (to - from) / 3600000;
    const range = { from, to, hours, key: 'custom' };
    setSelectedKey('custom');
    setCurrentRange(range);
    setIsOpen(false);
    onChange?.(range);
  };

  /**
   * Navigates the current range backward or forward in time
   */
  const navigate = (direction) => {
    if (!currentRange) return;
    const span = currentRange.to.getTime() - currentRange.from.getTime();
    const shift = direction === 'back' ? -span / 2 : span / 2;
    const from = new Date(currentRange.from.getTime() + shift);
    const to = new Date(currentRange.to.getTime() + shift);

    // Don't navigate into the future
    if (to > new Date()) return;

    const hours = (to - from) / 3600000;
    const range = { from, to, hours, key: 'custom' };
    setSelectedKey('custom');
    setCurrentRange(range);
    setCustomFrom(toLocalDatetime(from));
    setCustomTo(toLocalDatetime(to));
    onChange?.(range);
  };

  /**
   * Doubles the visible time span (zoom out) centered on current midpoint
   */
  const zoomOut = () => {
    if (!currentRange) return;
    const mid = (currentRange.from.getTime() + currentRange.to.getTime()) / 2;
    const halfSpan = currentRange.to.getTime() - currentRange.from.getTime();
    const from = new Date(mid - halfSpan);
    const to = new Date(Math.min(mid + halfSpan, Date.now()));
    const hours = (to - from) / 3600000;
    const range = { from, to, hours, key: 'custom' };
    setSelectedKey('custom');
    setCurrentRange(range);
    setCustomFrom(toLocalDatetime(from));
    setCustomTo(toLocalDatetime(to));
    onChange?.(range);
  };

  return (
    <div className="time-range-selector" ref={panelRef}>
      <div className="time-range-controls">
        <button className="nav-btn" onClick={() => navigate('back')} title="Shift back">
          <FaChevronLeft />
        </button>
        <button className="nav-btn" onClick={zoomOut} title="Zoom out">
          <FaSearchMinus />
        </button>
        <button className="nav-btn" onClick={() => navigate('forward')} title="Shift forward">
          <FaChevronRight />
        </button>

        <button
          className={`range-display-btn ${isOpen ? 'active' : ''} ${selectedKey === 'realtime' ? 'realtime' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          title={rangeSummary}
        >
          <FaClock />
          <span>{selectedKey === 'custom' && rangeSummary ? `Custom: ${rangeSummary}` : getDisplayLabel(selectedKey)}</span>
          <FaFilter />
        </button>
      </div>

      {isOpen && (
        <div className="time-range-panel">
          {/* Custom range inputs */}
          <div className="custom-range">
            <div className="custom-inputs">
              <label>
                <span>From</span>
                <div className="input-wrapper">
                  <FaCalendarAlt />
                  <input
                    type="datetime-local"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </div>
              </label>
              <label>
                <span>To</span>
                <div className="input-wrapper">
                  <FaCalendarAlt />
                  <input
                    type="datetime-local"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
              </label>
            </div>
            <button className="apply-btn" onClick={applyCustomRange}>Apply</button>
          </div>

          {/* Preset groups */}
          <div className="preset-groups">
            {PRESET_GROUPS.map(group => (
              <div key={group.label} className="preset-group">
                <div className="group-label">{group.label}</div>
                <div className="group-presets">
                  {group.presets.map(preset => (
                    <button
                      key={preset.key}
                      className={`preset-btn ${selectedKey === preset.key ? 'selected' : ''}`}
                      onClick={() => applyPreset(preset.key)}
                    >
                      {preset.label.replace(/^Last |^This |^Previous |^Today|^Yesterday/, (m) => {
                        // Show just the short part for the button
                        if (group.label === 'Last') return '';
                        return m;
                      }).trim() || preset.label.replace('Last ', '')}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// Display name for React DevTools (required for forwardRef components)
TimeRangeSelector.displayName = 'TimeRangeSelector';

export default TimeRangeSelector;
